import { motion, AnimatePresence } from "framer-motion";
import { Settings } from 'lucide-react';
import { IoCaretDown, IoHeart } from "react-icons/io5";
import { FaRegTrashCan } from "react-icons/fa6";
import { IoMdCloseCircle } from "react-icons/io";
import ClipLoader from "react-spinners/ClipLoader";
import useSelectVideo from "../hooks/useSelectVideo";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Fragment, useState, useEffect, useRef, useCallback } from "react";

import {
    readNotification,
    getAllNotification,
    acceptFollowRequestNotification,
    rejectFollowRequestNotification,
    readAllNotifications,
    deleteNotification,
    deleteAllNotifications,
    deleteSelectedNotifications,
    acceptGroupInviteNotification,
    rejectGroupInviteNotification,
} from "../redux/ApiSlice/NotificationSlice";
import { useNotificationUnreadCount } from "../context/SocketContext";
import { UserAccountFollow } from "../redux/ApiSlice/AccountProfileSlice";
import { IoMdArrowBack } from "react-icons/io";
import { selectVideoById } from "../redux/ApiSlice/WatchVideoSlice";
import toast from "react-hot-toast";

const filterTypeMap = {
    all: null,
    likes: 'post_like',
    comments: 'post_comment',
    'follow requests': 'user_follow_request',
    'Groups': 'group_invite',
    'Tags': 'news_tagged',
};

const Notification = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const selectVideo = useSelectVideo();
    const location = useLocation();
    const dropdownRef = useRef(null);
    const listRef = useRef(null);
    const fetchingRef = useRef(false);
    const [selectedFilter, setSelectedFilter] = useState("all");
    const [page, setPage] = useState(1);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedNotifications, setSelectedNotifications] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [hasNewNotifications, setHasNewNotifications] = useState(false);
    const pageInitializedRef = useRef(false);

    const { notificationData, loading, loadingMore, hasMore, error } = useSelector((state) => state.Notification);
    const notificationUnreadCountWs = useNotificationUnreadCount();
    const notificationUnreadCount = notificationUnreadCountWs?.data || { unread_count: 0 };

    useEffect(() => {
        setNotifications(notificationData?.notifications || []);
    }, [notificationData]);

    const handleFilterChange = (filter) => {
        setSelectedFilter(filter);
        setIsDropdownOpen(false);
        setPage(1);
        fetchingRef.current = false;
        dispatch(getAllNotification({ type: filterTypeMap[filter], page: 1 }));
    };

    const refreshNotifications = useCallback(() => {
        setPage(1);
        fetchingRef.current = false;
        dispatch(getAllNotification({ type: filterTypeMap[selectedFilter], page: 1 }));
    }, [dispatch, selectedFilter]);

    const handleAcceptRequest = (notificationId) => {
        dispatch(acceptFollowRequestNotification(notificationId))
            .then(() => refreshNotifications());
    };

    const handleRejectRequest = (notificationId) => {
        dispatch(rejectFollowRequestNotification(notificationId))
            .then(() => refreshNotifications());
    };

    const handleReadAllNotifications = useCallback(() => {
        pageInitializedRef.current = false;
        setHasNewNotifications(false);
        dispatch(readAllNotifications())
            .then(() => {
                dispatch(getAllNotification({ page: 1 }));
                pageInitializedRef.current = true;
            });
    }, [dispatch]);

    const handleDeleteNotification = (notificationId) => {
        dispatch(deleteNotification(notificationId))
            .then((result) => {
                if (result.type.endsWith('/fulfilled')) {
                    refreshNotifications();
                }
            })
            .catch((error) => {
                console.error('Error deleting notification:', error);
            });
    };

    const handleDeleteAllNotifications = () => {
        dispatch(deleteAllNotifications())
            .then((result) => {
                if (result.type.endsWith('/fulfilled')) {
                    refreshNotifications();
                }
            })
            .catch((error) => {
                console.error('Error deleting all notifications:', error);
            });
    };
    const handleAcceptGroupInvite = (notificationId) => {
        dispatch(acceptGroupInviteNotification(notificationId))
            .then(() => refreshNotifications());
    }

    const handleRejectGroupInvite = (notificationId) => {
        dispatch(rejectGroupInviteNotification(notificationId))
            .then(() => refreshNotifications());
    };

    const handleToggleSelection = (notificationId) => {
        setSelectedNotifications(prev => {
            const currentSelection = prev || [];
            if (currentSelection.includes(notificationId)) {
                return currentSelection.filter(id => id !== notificationId);
            } else {
                return [...currentSelection, notificationId];
            }
        });
    };

    const handleSelectAll = () => {
        const currentSelection = selectedNotifications || [];
        if (currentSelection.length === notifications.length) {
            setSelectedNotifications([]);
        } else {
            setSelectedNotifications(notifications.map(n => n.id));
        }
    };

    const handleGetPostDetailsById = useCallback(async (postId) => {
        try {
            const res = await dispatch(selectVideoById(postId));
            if (res.payload.detail) {
                toast.error("Video not Available");
            }
            if (res.payload && res.payload.video_url) {
                selectVideo(postId, [res.payload], location.pathname);
            }
        } catch (error) {
            console.error("Error selecting video by ID:", error);
        }

    }, [dispatch, selectVideo, location.pathname]);

    const handleNavigate = async (postId, id, type, groupId) => {
        if (!isSelectionMode) {
            dispatch(readNotification(id));
            if (type === "group_warning") {
                navigate(`/group/${groupId}/post/${postId}/comment`);
                return;
            } else if (type === "news_tagged") {
                navigate(`/neighborhood/news/${postId}`);
            }
            else {
                handleGetPostDetailsById(postId);
                // handleSelectVideo(postId);
                // navigate(`/watch-video/${postId}`);
            }
        }
    };

    useEffect(() => {
        handleReadAllNotifications();
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleReadAllNotifications]);

    const handleDeleteNotifications = () => {
        if (selectedNotifications && selectedNotifications.length > 0) {
            dispatch(deleteSelectedNotifications(selectedNotifications))
                .then((result) => {
                    if (result.type.endsWith('/fulfilled')) {
                        refreshNotifications();
                        setSelectedNotifications([]);
                        setIsSelectionMode(false);
                    }
                })
                .catch((error) => {
                    console.error('Error deleting notifications:', error);
                });
        }
    };

    const handleFollow = async (id) => {
        await dispatch(UserAccountFollow(id));
        refreshNotifications();
    };

    const handleViewLikes = (notification) => {
        navigate("/notification/likes", {
            state: {
                likeUsers: notification.like_preview_users,
                likeCount: notification.like_count,
            },
        });
    };

    useEffect(() => {
        const list = listRef.current;
        if (!list) return;

        const handleScroll = () => {
            if (loading || loadingMore || !hasMore || fetchingRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = list;
            if (scrollHeight - scrollTop - clientHeight < 150) {
                fetchingRef.current = true;
                const nextPage = page + 1;
                setPage(nextPage);
                dispatch(getAllNotification({ type: filterTypeMap[selectedFilter], page: nextPage }))
                    .finally(() => { fetchingRef.current = false; });
            }
        };

        list.addEventListener('scroll', handleScroll);
        return () => list.removeEventListener('scroll', handleScroll);
    }, [loading, loadingMore, hasMore, page, selectedFilter, dispatch]);

    useEffect(() => {
        if (!pageInitializedRef.current) return;
        if (notificationUnreadCount.unread_count > 0) {
            setHasNewNotifications(true);
        }
    }, [notificationUnreadCount.unread_count]);

    return (
        <Fragment>
            <div className="sticky top-0 z-10">
                <div className="grid grid-cols-12 h-12 bg-[#BDE6FF] rounded-b-xl justify-center items-center px-2">
                    <div className="col-span-2 flex justify-start">
                        <IoMdArrowBack
                            onClick={() => navigate(-1)}
                            className="text-[24px] cursor-pointer text-black dark:text-black"
                        />
                    </div>
                    <div className="col-span-8 flex items-center justify-center gap-x-1.5">
                        <p className="text-center text-[18px] font-bold text-black dark:text-black">
                            Notifications
                        </p>
                        {notificationUnreadCount?.unread_count > 0 && (
                            <div className="h-4 w-4 bg-red-500 flex items-center justify-center rounded-full">
                                <p className="text-[8px] text-black dark:text-black">
                                    {notificationUnreadCount.unread_count}
                                </p>
                            </div>
                        )}
                    </div>
                    <div className="col-span-2 flex items-center gap-x-2 justify-end">
                        <Settings
                            size={20}
                            onClick={() => navigate("/settings/notifications")}
                            className="text-black dark:text-black cursor-pointer"
                        />
                        {!isSelectionMode ?
                            <FaRegTrashCan
                                onClick={() => setIsSelectionMode(!isSelectionMode)}
                                className="text-[18px] text-black dark:text-black cursor-pointer"
                            />
                            :
                            <IoMdCloseCircle
                                onClick={() => setIsSelectionMode(!isSelectionMode)}
                                className="text-[22px] text-black dark:text-black cursor-pointer"
                            />
                        }
                    </div>
                </div>

                <div className="pb-2 bg-white dark:bg-[#1E1D21] flex items-center justify-between">
                    <div className="relative mt-2 px-2 w-1/2" ref={dropdownRef}>
                        <div
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="w-full flex items-center justify-between gap-x-2 bg-gray-200 dark:bg-[#333333] text-black dark:text-white px-2 py-1 rounded cursor-pointer"
                        >
                            <span className="text-[12px]">{selectedFilter.charAt(0).toUpperCase() + selectedFilter.slice(1)}</span>
                            <motion.div
                                animate={{ rotate: isDropdownOpen ? 180 : 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                <IoCaretDown className="text-[14px]" />
                            </motion.div>
                        </div>
                        {isDropdownOpen && (
                            <div className="absolute left-2 w-fit bg-white dark:bg-[#333333] border border-gray-200 dark:border-[#444444] shadow-md rounded mt-1 z-20 overflow-hidden">
                                {["all", "comments", "likes", "follow requests", "Groups", "Tags"].map((filter, index, array) => (
                                    <div
                                        key={filter}
                                        onClick={() => handleFilterChange(filter)}
                                        className={`pl-2 pr-[3.2rem] py-2 text-black dark:text-white hover:bg-gray-800 hover:text-white cursor-pointer text-[12px] border-b border-gray-200 dark:border-[#444444] whitespace-nowrap 
                                            ${index === array.length - 1 ? 'border-b-0' : ''}`
                                        }
                                    >
                                        {filter.charAt(0).toUpperCase() + filter.slice(1)}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {/* <div className="flex flex-col">
                        {filteredNotifications.length > 0 && (
                            <p
                                onClick={handleReadAllNotifications}
                                className="text-[10px] pr-2 pt-2 text-blue-500 cursor-pointer"
                            >
                                Mark All Read
                            </p>
                        )}
                    </div> */}
                </div>

                {isSelectionMode && (
                    <div className="bg-gray-100 dark:bg-gray-900 px-2 py-2 border-b border-gray-300 dark:border-gray-800/50 z-10">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-x-2">
                                <button
                                    onClick={handleSelectAll}
                                    className="text-[12px] bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded"
                                >
                                    {(selectedNotifications || []).length === notifications.length ? 'Deselect All' : 'Select All'}
                                </button>
                            </div>
                            {/* <div className="flex items-center gap-x-2">
                                <button
                                    onClick={handleDeleteAllNotifications}
                                    className="text-[12px] bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded"
                                >
                                    Delete All
                                </button>
                            </div> */}
                            <button
                                onClick={handleDeleteNotifications}
                                disabled={(selectedNotifications || []).length === 0}
                                className={`px-3 py-1 rounded-md text-[12px] flex items-center gap-x-1 
                                ${(selectedNotifications || []).length === 0
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-red-500 hover:bg-red-600 text-white'
                                    }`}>
                                <FaRegTrashCan className="text-[14px]" />
                                Delete
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <AnimatePresence>
                {hasNewNotifications && (
                    <motion.div
                        key="new-notif-banner"
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -20, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="flex justify-center py-2 bg-white dark:bg-[#1E1D21]"
                    >
                        <button
                            onClick={handleReadAllNotifications}
                            className="bg-black dark:bg-white text-white dark:text-black text-[12px] font-semibold px-4 py-1.5 rounded-full shadow-md flex items-center gap-x-1.5"
                        >
                            <span>↑</span>
                            <span>New notifications</span>
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            <div ref={listRef} className="mt-1 h-full pb-5 overflow-y-scroll overscroll-x-none">
                {loading ? (
                    <div className="flex justify-center items-center mt-10">
                        <ClipLoader
                            size={30}
                            color={"#cad2c5"}
                        />
                    </div>
                ) : notifications.length > 0 ? (
                    notifications.map((notification) => (
                        <div className="grid grid-cols-12 items-center px-2 py-2 border-b border-gray-300 dark:border-gray-800/50" key={notification.id}>
                            {isSelectionMode && (
                                <div className="col-span-1 flex items-center justify-center">
                                    <input
                                        type="checkbox"
                                        checked={(selectedNotifications || []).includes(notification.id)}
                                        onChange={() => handleToggleSelection(notification.id)}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                    />
                                </div>
                            )}
                            <div className="col-span-2">
                                {notification.type_of_notification === "post_like" && notification.like_preview_users?.length > 1 ? (
                                    <div
                                        className="relative w-10 h-10 cursor-pointer"
                                        onClick={() => handleViewLikes(notification)}
                                    >
                                        <img
                                            alt="notification"
                                            src={notification.like_preview_users[1].avatar_url}
                                            className="absolute top-0 left-0 w-8 h-8 rounded-full object-cover border border-gray-300 dark:border-gray-800/50"
                                        />
                                        <img
                                            alt="notification"
                                            src={notification.like_preview_users[0].avatar_url}
                                            className="absolute bottom-0 right-0 w-8 h-8 rounded-full object-cover border-2 border-white dark:border-[#1E1D21]"
                                        />
                                        <div className="absolute bottom-[-2px] right-[-2px] h-4 w-4 bg-red-500 rounded-full flex items-center justify-center border-2 border-white dark:border-[#1E1D21]">
                                            <IoHeart className="text-white text-[8px]" />
                                        </div>
                                    </div>
                                ) : (
                                    <img
                                        alt="notification"
                                        src={notification?.group?.group_profile_picture_url || notification.created_by.avatar_url}
                                        onClick={() => navigate(notification?.group?.id ? `/group/${notification?.group?.id}` : `/account/${notification.created_by.id}`)}
                                        className="w-10 h-10 rounded-full object-cover border border-gray-300 dark:border-gray-800/50 cursor-pointer"
                                    />
                                )}
                            </div>
                            <div className={isSelectionMode ? "col-span-5 ml-[-2px] flex flex-col justify-center" : "col-span-6 ml-[-2px] flex flex-col justify-center"}>
                                {notification.type_of_notification === "post_like" && notification.like_preview_users?.length > 1 ? (
                                    (() => {
                                        const previewNames = notification.like_preview_users.slice(0, 2).map(u => u.full_name);
                                        const remaining = (notification.like_count || 0) - previewNames.length;
                                        const likedSuffix = notification.body?.includes("liked ")
                                            ? notification.body.substring(notification.body.indexOf("liked "))
                                            : "liked your post.";
                                        return (
                                            <>
                                                <p
                                                    onClick={() => handleViewLikes(notification)}
                                                    className="text-[11px] leading-[15px] line-clamp-2 dark:text-white font-medium cursor-pointer"
                                                >
                                                    <span className="font-semibold">{previewNames[0]}</span>
                                                    {previewNames[1] && (
                                                        <>
                                                            {remaining > 0 ? ', ' : ' and '}
                                                            <span className="font-semibold">{previewNames[1]}</span>
                                                        </>
                                                    )}
                                                    {remaining > 0 && ` and ${remaining} other${remaining > 1 ? 's' : ''}`}
                                                    {' '}{likedSuffix}
                                                </p>
                                                <p className="text-[10px] line-clamp-1 leading-[14px] text-light-blue-500 font-medium mt-0.5">
                                                    {notification.time_since_created}
                                                </p>
                                            </>
                                        );
                                    })()
                                ) : notification.type_of_notification === "post_comment" ? (
                                    (() => {
                                        const name = notification?.group?.group_name || notification.created_by.full_name;
                                        const commentText = notification.body.split(":").slice(1).join(":").replace(/"/g, '').trim();
                                        return (
                                            <>
                                                <p
                                                    onClick={() => navigate(notification?.group?.id ? `/group/${notification?.group?.id}` : `/account/${notification.created_by.id}`)}
                                                    className="text-[11px] leading-[15px] line-clamp-1 dark:text-white font-medium cursor-pointer"
                                                >
                                                    <span className="font-semibold">{name}</span> commented
                                                </p>
                                                <p className="text-[10px] line-clamp-2 leading-[14px] text-gray-500 font-medium mt-0.5">
                                                    "{commentText}"
                                                </p>
                                                <p className="text-[10px] line-clamp-1 leading-[14px] text-light-blue-500 font-medium mt-0.5">
                                                    {notification.time_since_created}
                                                </p>
                                            </>
                                        );
                                    })()
                                ) : (
                                    (() => {
                                        const body = notification.body || "";
                                        // Bold whatever precedes the action verb in `body` itself, rather than
                                        // requiring an exact match with created_by.full_name — the API sometimes
                                        // uses a shorter/different display name in `body` (e.g. "Harry" vs "Harry J").
                                        const actionMatch = body.match(/\b(liked|reposted|started following|accepted|commented|requested)\b/i);
                                        const hasName = !!actionMatch && actionMatch.index > 0;
                                        const name = hasName ? body.slice(0, actionMatch.index).trim() : "";
                                        const suffix = hasName ? body.slice(actionMatch.index) : body;
                                        return (
                                            <>
                                                <p
                                                    onClick={() => navigate(notification?.group?.id ? `/group/${notification?.group?.id}` : `/account/${notification.created_by.id}`)}
                                                    className="text-[11px] leading-[15px] line-clamp-2 dark:text-white font-medium cursor-pointer"
                                                >
                                                    {hasName ? (
                                                        <><span className="font-semibold">{name}</span> {suffix}</>
                                                    ) : (
                                                        body
                                                    )}
                                                </p>
                                                <p className="text-[10px] line-clamp-1 leading-[14px] text-light-blue-500 font-medium mt-0.5">
                                                    {notification.time_since_created}
                                                </p>
                                            </>
                                        );
                                    })()
                                )}
                            </div>
                            <div className="col-span-4 relative flex justify-end items-center rounded-md">
                                {(notification.video_url || notification.post_id || notification?.news?.post_image_url) ? (
                                    <div className="relative group">
                                        {!notification?.news?.post_image_url && notification.video_url &&
                                            <img
                                                alt="notification"
                                                src={notification.thumbnail_url}
                                                onClick={() => handleNavigate(notification.post_id, notification.id, notification.type_of_notification, notification.group?.id)}
                                                className={`w-[3rem] h-[4rem] rounded object-cover bg-gray-100 ${!isSelectionMode ? 'cursor-pointer' : ''}`}
                                            />
                                        }
                                        {!notification.video_url && (notification?.news?.post_image_url || notification.img_url) &&
                                            <img
                                                alt="notification"
                                                src={notification?.news?.post_image_url || notification.img_url}
                                                onClick={() => handleNavigate(notification.news?.id, notification.id, notification.type_of_notification, notification.group?.id)}
                                                className={`w-[3rem] h-[4rem] rounded object-cover bg-gray-100 ${!isSelectionMode ? 'cursor-pointer' : ''}`}
                                            />
                                        }
                                        {!isSelectionMode && (
                                            <div className="hidden group-hover:block">
                                                <div className="absolute right-1 top-1 h-5 w-5 flex items-center justify-center bg-white bg-opacity-80 p-0.5 rounded-full hover:bg-opacity-100">
                                                    <FaRegTrashCan
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteNotification(notification.id);
                                                        }}
                                                        className="text-[10px] text-red-500 cursor-pointer"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        {/* <div className="absolute left-0 top-0 bg-black bg-opacity-50 px-1 py-0.5 rounded-br-md rounded-tl-md">
                                            {notification.type_of_notification === "post_like" && (
                                                <BiLike className="text-[12px] text-white" />
                                            )}
                                            {notification.type_of_notification === "post_comment" && (
                                                <BiCommentDetail className="text-[12px] text-white" />
                                            )}
                                        </div> */}
                                    </div>
                                ) : (
                                    (notification.requested && notification.is_follower) || notification.type_of_notification === "group_invite" ? <div className="flex items-center justify-end gap-x-2">
                                        <button
                                            onClick={() => notification.type_of_notification === "group_invite" ? handleAcceptGroupInvite(notification.id) : handleAcceptRequest(notification.id)}
                                            className="bg-black dark:bg-gray-800 hover:bg-cyan-500 text-white px-2 py-1 rounded-md text-[10px]">
                                            Accept
                                        </button>
                                        <button
                                            onClick={() => notification.type_of_notification === "group_invite" ? handleRejectGroupInvite(notification.id) : handleRejectRequest(notification.id)}
                                            className="bg-black dark:bg-gray-800 hover:bg-red-500 text-white px-2 py-1 rounded-md text-[10px]">
                                            Reject
                                        </button>
                                        {/* {!isSelectionMode && (
                                            <FaRegTrashCan
                                                onClick={() => handleDeleteNotification(notification.id)}
                                                className="text-[12px] text-red-500 cursor-pointer hover:text-red-600"
                                            />
                                        )} */}
                                    </div>
                                        :
                                        <div className="flex items-center justify-end gap-x-2">
                                            <button
                                                onClick={() => handleFollow(notification.created_by.id)}
                                                className={` ${notification.is_following && !notification.requested ? "bg-gray-700 dark:bg-[#333333] text-white" : "bg-black dark:bg-[#333333] text-white"} px-2 py-1 rounded-md text-[10px]`}>
                                                {notification.is_following && !notification.requested ? "Following" : "Follow Back"}
                                            </button>
                                        </div>
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    <p className="text-center text-[12px] text-gray-500 mt-8">
                        No notifications found for "{selectedFilter.charAt(0).toUpperCase() + selectedFilter.slice(1)}"
                    </p>
                )}
                {loadingMore && (
                    <div className="flex justify-center items-center py-4">
                        <ClipLoader size={20} color={"#cad2c5"} />
                    </div>
                )}
            </div>
        </Fragment>
    )
}

export default Notification;